import React, { useState } from 'react';
import { LogOut, Trash2, User, CreditCard, AlertTriangle } from 'lucide-react';
import CreditCounter from './CreditCounter';
import { useCredits } from '../contexts/CreditContext';
import { getApiUrl } from '../config';

const UserProfile = ({ user, onLogout }) => {
  const { fetchCredits } = useCredits();
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(getApiUrl('/logout'), {
        method: 'POST',
        credentials: 'include',
      });
      
      // Call onLogout regardless of response to ensure frontend state is cleared
      onLogout();
    } catch (error) {
      console.error('Logout error:', error);
      // Still call onLogout even if backend call fails
      onLogout();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      setIsDeleting(false);
      return;
    }

    try {
      const response = await fetch(getApiUrl('/user/delete-account'), {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        onLogout();
      } else {
        console.error('Failed to delete account');
      }
    } catch (error) {
      console.error('Delete account error:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!user) return null;

  return (
    <div className="bg-gray-900 rounded-xl shadow-lg p-6 max-w-md mx-auto border border-gray-700" style={{ boxShadow: '0 0 15px rgba(123, 97, 255, 0.3)' }}>
      <div className="flex items-center space-x-4 mb-6">
        <div className="bg-gray-800 p-3 rounded-full border border-gray-600">
          <User className="h-6 w-6 text-[#7B61FF]" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-white">{user.username}</h2>
          <p className="text-sm text-gray-400">{user.email}</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Credits Display - HIDDEN */}
        {/* <CreditCounter /> */}

        {/* Account Info */}
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-700">
            <span className="text-sm text-gray-400">Member since</span>
            <span className="text-sm font-medium text-white">
              {new Date(user.created_at).toLocaleDateString()}
            </span>
          </div>
          
          {user.last_login && (
            <div className="flex justify-between items-center py-2 border-b border-gray-700">
              <span className="text-sm text-gray-400">Last login</span>
              <span className="text-sm font-medium text-white">
                {new Date(user.last_login).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-2 pt-4">
          {/* Refresh Credits Button - HIDDEN */}
          {/* <button
            onClick={fetchCredits}
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-600 rounded-xl shadow-sm text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7B61FF] transition-colors duration-300"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Refresh Credits
          </button> */}
          
          <button
            onClick={handleLogout}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 transition-colors duration-300"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <LogOut className="h-4 w-4 mr-2" />
            )}
            Sign Out
          </button>

          {/* Delete Account Button - Less visible */}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-600 rounded-xl shadow-sm text-sm font-medium text-gray-500 bg-gray-800 hover:bg-gray-700 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-300"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Account
          </button>
        </div>
      </div>

      {/* Credit Usage Info */}
      {/* Credit Usage Information - HIDDEN */}
      {/* <div className="mt-6 p-4 bg-gray-800 rounded-xl border border-gray-600">
        <h3 className="text-sm font-medium text-white mb-2">Credit Usage</h3>
        <div className="text-xs text-gray-400 space-y-1">
          <div>• PowerPoint Generation: 1 credit</div>
          <div>• FAQ Generation: 1 credit</div>
          <div>• Battlecard Generation: 1 credit</div>
          <div>• Sales Call Practice: 1 credit</div>
          <div>• Architecture Generation: 1 credit</div>
          <div>• LMS Course Creation: 1 credit</div>
        </div>
      </div> */}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-xl p-6 max-w-md mx-4 border border-gray-700" style={{ boxShadow: '0 0 20px rgba(123, 97, 255, 0.4)' }}>
            <h3 className="text-lg font-semibold text-white mb-4">Delete Account</h3>
            <p className="text-gray-400 mb-6">
              Are you sure you want to delete your account? This action cannot be undone and will permanently remove all your data.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-600 rounded-xl text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7B61FF] transition-colors duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 border border-transparent rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 transition-colors duration-300"
              >
                {isDeleting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-auto"></div>
                ) : (
                  'Delete Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile; 